/**
 * Extract domain from a URL
 * 
 * @param url - URL string to extract domain from (with or without protocol)
 * @returns Clean domain name without protocol or www prefix
 * 
 * @example
 * extractDomain('https://www.example.com.au') // Returns 'example.com.au'
 * extractDomain('www.example.com') // Returns 'example.com'
 * extractDomain('example.com') // Returns 'example.com'
 */
export function extractDomain(url: string): string {
  try {
    // Add protocol if missing
    if (!url.match(/^https?:\/\//)) {
      url = 'https://' + url;
    }
    
    const urlObj = new URL(url);
    return urlObj.hostname.replace(/^www\./, '');
  } catch {
    // If URL parsing fails, try to extract domain manually
    const match = url.match(/^(?:https?:\/\/)?(?:www\.)?([^\/]+)/);
    return match ? match[1] : url;
  }
}

/**
 * Generate likely domain variations from a company name
 * Uses multiple strategies to create potential domain names for Australian businesses
 * 
 * @param name - Company name to generate domains from
 * @returns Array of potential domain names with various TLDs
 * 
 * @example
 * guessCompanyDomain('ABC Construction Pty Ltd')
 * // Returns: ['abcconstruction.com.au', 'abcconstruction.net.au', 'abc.com.au', ...]
 * 
 * @example
 * guessCompanyDomain('Smith & Co Builders')
 * // Returns: ['smithbuilders.com.au', 'smith.net.au', 'scb.com.au', ...]
 */
export function guessCompanyDomain(name: string): string[] {
  if (!name) return [];
  
  // Remove common company suffixes
  const cleanName = name
    .toLowerCase()
    .replace(/\b(?:pty|ltd|limited|inc|incorporated|corp|corporation|co|company)\b/gi, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .trim();
  
  // Extract words
  const words = cleanName.split(/\s+/).filter(word => word.length > 1);
  
  const domains: string[] = [];
  
  // Common TLDs for Australian businesses
  const tlds = ['.com.au', '.net.au', '.org.au', '.com', '.net', '.org'];
  
  // Strategy 1: Full company name (without spaces)
  const fullName = words.join('');
  tlds.forEach(tld => {
    domains.push(fullName + tld);
  });
  
  // Strategy 2: First word only (if more than one word)
  // Skip very short tokens like "lf" to avoid bad matches (e.g. lf.net)
  if (words.length > 1 && words[0].length >= 4) {
    tlds.forEach(tld => {
      domains.push(words[0] + tld);
    });
  }
  
  // Strategy 3: First two words (if more than two words)
  if (words.length > 2) {
    const firstTwo = words.slice(0, 2).join('');
    tlds.forEach(tld => {
      domains.push(firstTwo + tld);
    });
  }
  
  // Strategy 4: Common abbreviations
  const abbreviations = words.map(word => word[0]).join('');
  if (abbreviations.length >= 2 && abbreviations.length <= 5) {
    tlds.forEach(tld => {
      domains.push(abbreviations + tld);
    });
  }
  
  // Strategy 5: Building/Construction specific variations
  const industryWords = ['building', 'construction', 'construct', 'builders', 'group'];
  const hasIndustryWord = words.some(word => industryWords.includes(word));
  
  if (!hasIndustryWord) {
    // Add 'builders' to the end
    tlds.forEach(tld => {
      domains.push(fullName + 'builders' + tld);
      if (words.length > 1) {
        domains.push(words[0] + 'builders' + tld);
      }
    });
  }
  
  // Remove duplicates and return
  return [...new Set(domains)];
}

/**
 * Try to find company website from ABR data
 * Note: This is a placeholder - ABR API doesn't typically provide website URLs
 * You may need to integrate with other services or databases
 * 
 * @param abrData - ABR (Australian Business Register) data object
 * @returns Website URL if found, null otherwise
 * 
 * @example
 * const website = extractWebsiteFromABR(abrData);
 * // Currently always returns null - placeholder for future enhancement
 */
export function extractWebsiteFromABR(abrData: any): string | null {
  // ABR typically doesn't provide website information
  // This is a placeholder for future integration with other data sources
  return null;
}